import type { ChessRepsApi } from '../api/client';
import type { LineCreate, RepertoireLine } from '../api/types';
import { sameNode } from './navigation';

/** Consume server-parsed PGN moves; all legality and identity remain server-owned. */
export async function saveImportedVariation(
  api: ChessRepsApi,
  repertoireId: string,
  gameId: string,
  name: string,
  onProgress: (message: string) => void,
): Promise<RepertoireLine> {
  const game = await api.getGame(gameId);
  const repertoire = await api.getRepertoire(repertoireId);
  const root = repertoire.data.defaultRoot;
  if (!sameNode(game.initialPosition, root.position)) {
    throw new Error('This PGN starts from a different position than this repertoire. Import a line from the repertoire’s starting position.');
  }
  if (!game.movesUci.length || game.movesUci.length > 512) {
    throw new Error('A variation must contain between 1 and 512 half-moves.');
  }
  const annotations: NonNullable<LineCreate['annotations']> = [];
  let current = root.position;
  for (const [ply, uci] of game.movesUci.entries()) {
    onProgress(`Adding move ${ply + 1} of ${game.movesUci.length}…`);
    const node = await api.getNode(repertoireId, current.zobristHash, current.positionId);
    let edge = node.data.outgoing.find((candidate) => candidate.transition.uci === uci);
    if (!edge) {
      const etag = node.etag ?? (await api.getRepertoire(repertoireId)).etag;
      if (!etag) throw new Error('Could not load the repertoire version. Try saving the imported line again.');
      const opponent = (repertoire.data.side === 'white' && node.data.position.sideToMove === 'b') ||
        (repertoire.data.side === 'black' && node.data.position.sideToMove === 'w');
      edge = (await api.addMove(repertoireId, etag, crypto.randomUUID(), {
        fromFen: node.data.position.canonicalFen, uci,
        role: opponent ? 'opponent' : 'alternative', priority: 100,
      })).data;
    }
    if (!edge.enabled) throw new Error(`Move ${ply + 1} is disabled in this repertoire. Enable it before importing this line.`);
    annotations.push({ ply, role: edge.role, priority: edge.priority,
      ...(edge.comment == null ? {} : { comment: edge.comment }),
      ...(edge.maxLossCp == null ? {} : { maxLossCp: edge.maxLossCp }) });
    current = edge.transition.to;
  }
  onProgress('Saving the named variation…');
  const latest = await api.getRepertoire(repertoireId);
  if (!latest.etag) throw new Error('Could not load the repertoire version. Try saving the imported line again.');
  const result = await api.createLine(repertoireId, crypto.randomUUID(), latest.etag, {
    rootId: root.rootId, name, movesUci: game.movesUci, annotations,
  });
  return result.data.line;
}
